/**
 * Tiny colour helpers for the map layouts. Hex in, hex out; no dependency.
 */

/**
 * Parses a 6-digit hex colour.
 *
 * @param hex - A colour like "#8c1515".
 * @return Red, green and blue channels in 0..255.
 * @throws Error when the string is not a 6-digit hex colour.
 */
function parseHex(hex: string): [number, number, number] {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) {
    throw new Error(`Expected a 6-digit hex colour, got ${hex}`);
  }
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/**
 * Formats channels as a 6-digit hex colour.
 *
 * @param channels - Red, green and blue in 0..255; rounded and clamped.
 * @return A colour like "#8c1515".
 */
function toHex(channels: [number, number, number]): string {
  return (
    '#' +
    channels
      .map(channel => {
        const clamped = Math.max(0, Math.min(255, Math.round(channel)));
        return clamped.toString(16).padStart(2, '0');
      })
      .join('')
  );
}

/**
 * Linearly mixes two hex colours.
 *
 * @param from - Colour at amount 0.
 * @param to - Colour at amount 1.
 * @param amount - How far to move from `from` toward `to`, in [0, 1].
 * @return The mixed colour as hex.
 * @throws Error when either colour is not a 6-digit hex string.
 */
export function mix(from: string, to: string, amount: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  return toHex([
    a[0] + (b[0] - a[0]) * amount,
    a[1] + (b[1] - a[1]) * amount,
    a[2] + (b[2] - a[2]) * amount,
  ]);
}

/**
 * Lightens a colour toward white.
 *
 * @param hex - The colour to lighten.
 * @param amount - Fraction of the way to white, in [0, 1].
 * @return The lightened colour.
 * @throws Error when the colour is not a 6-digit hex string.
 */
export function lighten(hex: string, amount: number): string {
  return mix(hex, '#ffffff', amount);
}

/**
 * Darkens a colour toward black.
 *
 * @param hex - The colour to darken.
 * @param amount - Fraction of the way to black, in [0, 1].
 * @return The darkened colour.
 * @throws Error when the colour is not a 6-digit hex string.
 */
export function darken(hex: string, amount: number): string {
  return mix(hex, '#000000', amount);
}
